import {
  Badge,
  Button,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Text,
  Title3,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import type { BonusAlias } from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { MOBILE, SHRINKABLE } from '../responsive';

import { useTimeAdminApi } from './api';

/**
 * Mapping the production-bonus spreadsheet's names to real employees.
 *
 * 🔥 WHY THIS SCREEN EXISTS AT ALL. Measured against the 155 active employees in
 * production, of the 29 names on `Graysons_Stuff.xlsx` only SIX matched exactly — and
 * the near-misses are other people who appear on the SAME sheet: "Rob Guerrero" is four
 * edits from JORGE GUERRERO, "Chris Salley" five from CHRIS CLASSEN. Resolving those
 * automatically would pay one man's bonus to another, and the week would still total
 * $1,450, so nobody would find it.
 *
 * So the import refuses to guess, and a human decides here, once per name. The
 * decisions are remembered — the point is that this screen gets emptier every week, not
 * that somebody works through it every week.
 *
 * THE DROPDOWN IS ORDERED BY SIMILARITY AND SELECTS NOTHING. Same distances the import
 * refuses to act on, used to save scrolling 155 names for a person who knows these
 * people by sight. The difference is entirely in who decides.
 */

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', ...shorthands.gap('16px'), ...SHRINKABLE },
  header: { display: 'flex', alignItems: 'center', ...shorthands.gap('12px'), flexWrap: 'wrap' },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  rows: { display: 'flex', flexDirection: 'column', ...shorthands.gap('8px') },
  row: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.padding('10px', '12px'),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    [MOBILE]: { flexDirection: 'column', alignItems: 'stretch' },
  },
  name: { fontWeight: tokens.fontWeightSemibold, minWidth: '180px' },
  grow: { flexGrow: 1, minWidth: 0 },
  actions: { display: 'flex', ...shorthands.gap('8px'), flexShrink: 0 },
  empty: { ...shorthands.padding('24px'), textAlign: 'center', color: tokens.colorNeutralForeground3 },
});

/** Employee option, resolved from the directory. */
interface EmployeeOption {
  id: string;
  name: string;
}

export function BonusNamesTab(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();

  const [aliases, setAliases] = useState<BonusAlias[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, emps] = await Promise.all([api.listBonusAliases(), api.listEmployees()]);
      setAliases(rows);
      setEmployees(
        emps
          .map((e) => ({ id: e.id, name: e.displayName }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load the name mappings.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (name: string, employeeId: string | null): Promise<void> => {
    setBusy(name);
    try {
      await api.mapBonusAlias(name, employeeId);
      setNotice(
        employeeId === null
          ? `"${name}" recorded as not an employee — it will stop appearing here.`
          : `"${name}" mapped.`
      );
      setNewName('');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'That mapping could not be saved.');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (row: BonusAlias): Promise<void> => {
    setBusy(row.aliasDisplay);
    try {
      await api.deleteBonusAlias(row.id);
      setNotice(`Mapping for "${row.aliasDisplay}" removed.`);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'That mapping could not be removed.');
    } finally {
      setBusy(null);
    }
  };

  /*
   * Two employees reachable by the same spreadsheet name is not an error, but it is
   * almost always a mistake — two aliases pointed at one person usually means one of
   * them was mapped in haste. Worth showing rather than leaving to be discovered on a
   * paycheck.
   */
  const pending = useMemo(
    () => aliases.filter((a) => a.employeeId === null && !a.decided).length,
    [aliases]
  );

  const duplicateEmployeeIds = useMemo(() => {
    const seen = new Map<string, number>();
    for (const a of aliases) {
      if (a.employeeId !== null) {
        seen.set(a.employeeId, (seen.get(a.employeeId) ?? 0) + 1);
      }
    }
    return new Set([...seen.entries()].filter(([, n]) => n > 1).map(([id]) => id));
  }, [aliases]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Title3>Bonus spreadsheet names</Title3>
      </div>

      <Text className={styles.muted}>
        The production-bonus spreadsheet identifies people by nicknames and informal
        spellings, which do not reliably match an employee record. WorkLogix never guesses
        — a name it does not recognise imports nothing and appears here. Map it once and it
        is remembered.
      </Text>

      {error !== null ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}
      {notice !== null ? (
        <MessageBar intent="success">
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      ) : null}

      {/* Add a mapping ahead of the next import, rather than waiting to be told. */}
      <div className={styles.row}>
        <Field label="Spreadsheet name" className={styles.grow}>
          <Input
            value={newName}
            onChange={(_, d) => {
              setNewName(d.value);
            }}
            placeholder="e.g. Rob Guerrero"
          />
        </Field>
        <div className={styles.actions}>
          <Button
            appearance="primary"
            disabled={newName.trim() === '' || busy !== null}
            onClick={() => {
              // Added unmapped, then pointed at somebody on the row below. Two steps on
              // purpose: typing a name and choosing a person are different decisions.
              void save(newName.trim(), null);
            }}
          >
            Add
          </Button>
        </div>
      </div>

      {/*
        * What is WAITING, stated first. The screen's job is to empty itself, so the
        * count of unanswered names is the headline rather than something to infer from
        * a list of badges.
        */}
      {pending > 0 ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            {pending} name{pending === 1 ? '' : 's'} from the spreadsheet {pending === 1 ? 'is' : 'are'}{' '}
            waiting to be identified. Until then {pending === 1 ? 'it creates' : 'they create'} no
            bonus at all.
          </MessageBarBody>
        </MessageBar>
      ) : null}

      {loading ? (
        <Spinner label="Loading name mappings…" />
      ) : aliases.length === 0 ? (
        <div className={styles.empty}>
          <Text>
            No mappings yet. Run the import from the spreadsheet and any name WorkLogix does
            not recognise appears here automatically.
          </Text>
        </div>
      ) : (
        <div className={styles.rows}>
          {aliases.map((row) => (
            <div key={row.id} className={styles.row}>
              <Text className={styles.name}>{row.aliasDisplay}</Text>
              <div className={styles.grow}>
                <Dropdown
                  value={
                    row.employeeName ??
                    (row.decided ? 'Not an employee' : 'Choose who this is…')
                  }
                  // An undecided row selects NOTHING, so the dropdown does not look
                  // like somebody already answered it.
                  selectedOptions={row.employeeId !== null ? [row.employeeId] : row.decided ? ['none'] : []}
                  onOptionSelect={(_, d) => {
                    void save(row.aliasDisplay, d.optionValue === 'none' ? null : (d.optionValue ?? null));
                  }}
                  disabled={busy !== null}
                >
                  {/*
                    A first-class choice, not an absence. Recording "not an employee"
                    is what stops a machine nickname being re-reported every week.
                  */}
                  <Option value="none">Not an employee</Option>
                  {employees.map((e) => (
                    <Option key={e.id} value={e.id}>
                      {e.name}
                    </Option>
                  ))}
                </Dropdown>
              </div>
              {row.employeeId !== null && duplicateEmployeeIds.has(row.employeeId) ? (
                <Badge color="warning">Also mapped from another name</Badge>
              ) : null}
              {/*
                * Two very different states behind one NULL employee, and conflating them
                * is what made the import silently swallow unknown names. "Needs mapping"
                * is a question; "Ignored" is an answer.
                */}
              {row.employeeId === null && !row.decided ? (
                <Badge color="warning">Needs mapping</Badge>
              ) : null}
              {row.employeeId === null && row.decided ? (
                <Badge color="informative">Ignored</Badge>
              ) : null}
              <div className={styles.actions}>
                <Button disabled={busy !== null} onClick={() => void remove(row)}>
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
